import { HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { OrganisationNode } from '@coin/shared/org-tree/util';
import { HttpHelpersService } from '@coin/shared/core/data-access';
import { Organisation, Position, SpecialRole, SpecialRoleType } from '@coin/shared/core/util-models';
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { produce } from 'immer';
import { catchError, Observable, switchMap } from 'rxjs';
import {
  BusinessPartner as ApiBusinessPartner,
  CountryResponsibility as ApiCountryResponsibility,
  OpenJob,
  Organisation as ApiOrganisation,
  OrganisationsService,
  OrganisationTree,
  SpecialResponsibility as ApiSpecialResponsibility
} from '../../api/generated';
import { MappingHelpers } from '../../shared/helper/mapping-helpers';
import { LoadingError } from '../models/loading-error.enum';
import {
  BusinessPartnersLoaded,
  CountryResponsibilitiesLoaded,
  LoadBusinessPartners,
  LoadCountryResponsibilities,
  LoadOpenJobs,
  LoadOrganisationTree,
  LoadPositions,
  LoadRootOrganisation,
  LoadSpecialResponsibilities,
  OpenJobsLoaded,
  OrganisationTreeLoaded,
  PositionsLoaded,
  RootOrganisationLoaded,
  SpecialResponsibilitiesLoaded
} from './org-management.actions';

export interface OrgManagementStateModel {
  rootOrganisationId?: string;
  organisationNodes: { [organisationId: string]: OrganisationNode };
  organisationPaths: { [organisationId: string]: ApiOrganisation[] };
  organisationTreeLoading: boolean;
  organisationLoadingError: LoadingError;
  employeeRoles: { [organisationId: string]: SpecialRole[] };
  businessPartnersLoaded: { [organisationId: string]: boolean };
  businessPartnersLoading: boolean;
  specialResponsibilitiesLoaded: { [organisationId: string]: boolean };
  specialResponsibilitiesLoading: boolean;
  countryResponsibilitiesLoaded: { [organisationId: string]: boolean };
  countryResponsibilitiesLoading: boolean;
  positions: { [organisationId: string]: Position[] };
  positionsLoading: { [organisationId: string]: boolean };
  openJobs: { [organisationId: string]: OpenJob[] };
  openJobsLoading: { [organisationId: string]: boolean };
}

const STATE_DEFAULTS: OrgManagementStateModel = {
  organisationNodes: {},
  organisationPaths: {},
  organisationTreeLoading: false,
  organisationLoadingError: undefined,
  employeeRoles: {},
  businessPartnersLoaded: {},
  businessPartnersLoading: false,
  specialResponsibilitiesLoaded: {},
  specialResponsibilitiesLoading: false,
  countryResponsibilitiesLoaded: {},
  countryResponsibilitiesLoading: false,
  positions: {},
  positionsLoading: {},
  openJobs: {},
  openJobsLoading: {}
};

@State<OrgManagementStateModel>({
  name: 'OrgManagementState',
  defaults: STATE_DEFAULTS
})
@Injectable()
export class OrgManagementState {
  private organisationsService = inject(OrganisationsService);
  private httpHelper = inject(HttpHelpersService);

  @Selector()
  public static organisationTreeLoading(state: OrgManagementStateModel): boolean {
    return state.organisationTreeLoading;
  }

  @Selector()
  public static rootOrganisationId(state: OrgManagementStateModel): string {
    return state.rootOrganisationId;
  }

  @Selector()
  public static organisationNodes(state: OrgManagementStateModel): { [organisationId: string]: OrganisationNode } {
    return state.organisationNodes;
  }

  public static organisation(organisationId?: string): (state: OrgManagementStateModel) => Organisation {
    return createSelector(
      [OrgManagementState],
      (state: OrgManagementStateModel) => state.organisationNodes[organisationId]?.organisation
    );
  }

  public static organisationPath(organisationId?: string): (state: OrgManagementStateModel) => ApiOrganisation[] {
    return createSelector(
      [OrgManagementState],
      (state: OrgManagementStateModel) => state.organisationPaths[organisationId]
    );
  }

  @Selector()
  public static organisationLoadingError(state: OrgManagementStateModel): LoadingError {
    return state.organisationLoadingError;
  }

  @Selector()
  public static businessPartnersLoading(state: OrgManagementStateModel): boolean {
    return state.businessPartnersLoading;
  }

  @Selector()
  public static employeeRoles(state: OrgManagementStateModel): { [organisationId: string]: SpecialRole[] } {
    return state.employeeRoles;
  }

  @Selector()
  public static specialResponsibilitiesLoading(state: OrgManagementStateModel): boolean {
    return state.specialResponsibilitiesLoading;
  }

  @Selector()
  public static countryResponsibilitiesLoading(state: OrgManagementStateModel): boolean {
    return state.countryResponsibilitiesLoading;
  }

  @Selector()
  public static positions(state: OrgManagementStateModel): { [organisationId: string]: Position[] } {
    return state.positions;
  }

  @Selector()
  public static positionsLoading(state: OrgManagementStateModel): { [organisationId: string]: boolean } {
    return state.positionsLoading;
  }

  public static openJobsLoading(organisationId: string): (state: OrgManagementStateModel) => boolean {
    return createSelector(
      [OrgManagementState],
      (state: OrgManagementStateModel) => state.openJobsLoading[organisationId]
    );
  }

  public static openJobs(organisationId: string): (state: OrgManagementStateModel) => OpenJob[] {
    return createSelector([OrgManagementState], (state: OrgManagementStateModel) => state.openJobs[organisationId]);
  }

  @Action(LoadRootOrganisation)
  public loadRootOrganisation(ctx: StateContext<OrgManagementStateModel>): Observable<void> {
    ctx.patchState({ organisationTreeLoading: true, organisationLoadingError: undefined });

    return this.organisationsService.getRootOrganisationTree().pipe(
      this.httpHelper.handleError('Cannot get organisation tree'),
      switchMap(rootOrg => ctx.dispatch(new RootOrganisationLoaded(rootOrg)))
    );
  }

  @Action(RootOrganisationLoaded)
  public rootOrganisationLoaded(ctx: StateContext<OrgManagementStateModel>, { rootOrganisation }): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        state.rootOrganisationId = rootOrganisation.root.id;
        const updatedRootOrganisation = {
          ...rootOrganisation,
          root: rootOrganisation.root,
          children: rootOrganisation.children
        };
        state.organisationNodes[updatedRootOrganisation.root.id] =
          OrgManagementState.mapOrganisationNode(updatedRootOrganisation);
        state.organisationPaths[updatedRootOrganisation.root.id] = [
          ...updatedRootOrganisation.path,
          updatedRootOrganisation.root
        ];
        state.organisationTreeLoading = false;
      })
    );
  }

  @Action(LoadOrganisationTree)
  public loadOrganisationTree(ctx: StateContext<OrgManagementStateModel>, { organisationId }): Observable<void> {
    if (ctx.getState().organisationNodes[organisationId]) {
      return;
    }

    ctx.patchState({ organisationTreeLoading: true, organisationLoadingError: undefined });

    return this.organisationsService.getOrganisationTree(organisationId).pipe(
      catchError(response => {
        const loadingError = response.status === HttpStatusCode.NotFound ? LoadingError.NotFound : LoadingError.Other;
        ctx.patchState({ organisationTreeLoading: false, organisationLoadingError: loadingError });
        throw response;
      }),
      switchMap(orgTree => ctx.dispatch(new OrganisationTreeLoaded(organisationId, orgTree)))
    );
  }

  @Action(OrganisationTreeLoaded)
  public organisationTreeLoaded(
    ctx: StateContext<OrgManagementStateModel>,
    { organisationId, organisationTree }
  ): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        state.organisationNodes[organisationId] = OrgManagementState.mapOrganisationNode(organisationTree);
        state.organisationPaths[organisationId] = [...organisationTree.path, organisationTree.root];
        state.organisationTreeLoading = false;
        state.organisationLoadingError = undefined;
      })
    );
  }

  @Action(LoadBusinessPartners)
  public loadBusinessPartners(ctx: StateContext<OrgManagementStateModel>, { organisationId }): Observable<void> {
    if (ctx.getState().businessPartnersLoaded[organisationId]) {
      return;
    }

    ctx.patchState({ businessPartnersLoading: true });

    return this.organisationsService.getBusinessPartners(organisationId).pipe(
      this.httpHelper.handleError('Cannot get business partners for organisation'),
      switchMap(businessPartners => ctx.dispatch(new BusinessPartnersLoaded(organisationId, businessPartners.content)))
    );
  }

  @Action(BusinessPartnersLoaded)
  public businessPartnersLoaded(
    ctx: StateContext<OrgManagementStateModel>,
    { organisationId, businessPartners }
  ): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        state.businessPartnersLoaded[organisationId] = true;
        state.employeeRoles[organisationId] = [
          ...(state.employeeRoles[organisationId] ?? []),
          ...businessPartners.map(role => OrgManagementState.mapSpecialRole(role, SpecialRoleType.BusinessPartner))
        ];
        state.businessPartnersLoading = false;
      })
    );
  }

  @Action(LoadSpecialResponsibilities)
  public loadSpecialResponsibilities(ctx: StateContext<OrgManagementStateModel>, { organisationId }): Observable<void> {
    if (ctx.getState().specialResponsibilitiesLoaded[organisationId]) {
      return;
    }

    ctx.patchState({ specialResponsibilitiesLoading: true });

    return this.organisationsService.getSpecialResponsibilities(organisationId).pipe(
      this.httpHelper.handleError('Cannot get special responsibilities for organisation'),
      switchMap(specialResponsibilities =>
        ctx.dispatch(new SpecialResponsibilitiesLoaded(organisationId, specialResponsibilities.content))
      )
    );
  }

  @Action(SpecialResponsibilitiesLoaded)
  public specialResponsibilitiesLoaded(
    ctx: StateContext<OrgManagementStateModel>,
    { organisationId, specialResponsibilities }
  ): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        state.specialResponsibilitiesLoaded[organisationId] = true;
        state.employeeRoles[organisationId] = [
          ...(state.employeeRoles[organisationId] ?? []),
          ...specialResponsibilities.map(role =>
            OrgManagementState.mapSpecialRole(role, SpecialRoleType.SpecialResponsibility)
          )
        ];
        state.specialResponsibilitiesLoading = false;
      })
    );
  }

  @Action(LoadCountryResponsibilities)
  public loadCountryResponsibilities(ctx: StateContext<OrgManagementStateModel>, { organisationId }): Observable<void> {
    if (ctx.getState().countryResponsibilitiesLoaded[organisationId]) {
      return;
    }

    ctx.patchState({ countryResponsibilitiesLoading: true });

    return this.organisationsService.getCountryResponsibilities(organisationId).pipe(
      this.httpHelper.handleError('Cannot get country responsibilities for organisation'),
      switchMap(countryResponsibilities =>
        ctx.dispatch(new CountryResponsibilitiesLoaded(organisationId, countryResponsibilities.content))
      )
    );
  }

  @Action(CountryResponsibilitiesLoaded)
  public countryResponsibilitiesLoaded(
    ctx: StateContext<OrgManagementStateModel>,
    { organisationId, countryResponsibilities }
  ): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        state.countryResponsibilitiesLoaded[organisationId] = true;
        state.employeeRoles[organisationId] = [
          ...(state.employeeRoles[organisationId] ?? []),
          ...countryResponsibilities.map(role =>
            OrgManagementState.mapSpecialRole(role, SpecialRoleType.CountryResponsibility)
          )
        ];
        state.countryResponsibilitiesLoading = false;
      })
    );
  }

  @Action(LoadPositions)
  public loadPositions(ctx: StateContext<OrgManagementStateModel>, { organisationId }): Observable<void> {
    if (ctx.getState().positions[organisationId]) {
      return;
    }

    ctx.setState(
      produce(ctx.getState(), state => {
        state.positionsLoading[organisationId] = true;
      })
    );

    return this.organisationsService.getPositions(organisationId).pipe(
      this.httpHelper.handleError('Cannot get positions for organisation'),
      switchMap(positions => ctx.dispatch(new PositionsLoaded(organisationId, positions)))
    );
  }

  @Action(PositionsLoaded)
  public positionsLoaded(ctx: StateContext<OrgManagementStateModel>, { organisationId, positions }): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        state.positions[organisationId] = positions.map(MappingHelpers.mapPosition);
        state.positionsLoading[organisationId] = false;
      })
    );
  }

  @Action(LoadOpenJobs)
  public loadOpenJobs(ctx: StateContext<OrgManagementStateModel>, { organisationId }): Observable<void> {
    if (ctx.getState().openJobs[organisationId]) {
      return;
    }

    ctx.setState(
      produce(ctx.getState(), state => {
        state.openJobsLoading[organisationId] = true;
      })
    );

    return this.organisationsService.getOpenJobs(organisationId).pipe(
      this.httpHelper.handleError('Cannot get open jobs for organisation'),
      switchMap(openJobs => ctx.dispatch(new OpenJobsLoaded(organisationId, openJobs)))
    );
  }

  @Action(OpenJobsLoaded)
  public openJobsLoaded(ctx: StateContext<OrgManagementStateModel>, { organisationId, openJobs }): void {
    ctx.setState(
      produce(ctx.getState(), state => {
        state.openJobs[organisationId] = openJobs;
        state.openJobsLoading[organisationId] = false;
      })
    );
  }

  private static mapOrganisationNode(organisationTree: OrganisationTree): OrganisationNode {
    return {
      organisation: OrgManagementState.mapOrganisation(organisationTree.root),
      children: organisationTree.children?.map(OrgManagementState.mapOrganisation)
    };
  }

  private static mapOrganisation(organisation: ApiOrganisation): Organisation {
    return {
      ...organisation,
      heads: organisation.heads?.map(MappingHelpers.mapPosition)
    };
  }

  private static mapSpecialRole(
    specialRole: ApiBusinessPartner | ApiSpecialResponsibility | ApiCountryResponsibility,
    type: SpecialRoleType
  ): SpecialRole {
    return {
      title: specialRole.title,
      employee: MappingHelpers.mapEmployee(specialRole.employee),
      id: specialRole.employee.id,
      type
    };
  }
}
